import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ShieldCheck, Download, CreditCard, Plus } from "lucide-react";
import { listOrders } from "../lib/api";
import { useUser } from "../context/UserContext";
import { formatEUR, timeAgo } from "../lib/constants";
import StatusPill from "../components/StatusPill";

export default function Payments() {
  const { user } = useUser();
  const [orders, setOrders] = useState([]);

  useEffect(() => { listOrders().then(setOrders).catch(() => setOrders([])); }, [user?.id]);

  // Creator shouldn't see this; keep simple redirect-like notice
  if (user?.role === "creator") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="cc-overline">Payments</div>
        <h1 className="text-3xl font-semibold text-white mt-2 mb-6">This page is company-only</h1>
        <div className="cc-surface rounded-2xl p-6 text-sm text-[#a0a0b0]">
          Creators manage earnings from the <Link to="/wallet" className="text-[#00ff88] hover:underline">Wallet</Link> page.
        </div>
      </div>
    );
  }

  const inEscrow = orders.filter((o) => ["paid", "in_progress", "delivered", "revision_requested"].includes(o.status));
  const released = orders.filter((o) => o.status === "completed");
  const escrowTotal = inEscrow.reduce((a, o) => a + o.price, 0);
  const paidTotal = released.reduce((a, o) => a + o.price, 0);
  const volume = escrowTotal + paidTotal;

  return (
    <div data-testid="payments-page" className="max-w-7xl mx-auto px-6 py-12">
      <div className="cc-overline">Finance</div>
      <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-2 mb-10">Payments & Escrow</h1>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <FinanceCard testId="finance-escrow" label="In Escrow" value={formatEUR(escrowTotal)} sub={`${inEscrow.length} active orders`} icon={ShieldCheck} />
        <FinanceCard testId="finance-paid" label="Total Paid" value={formatEUR(paidTotal)} sub={`${released.length} released`} icon={Wallet} accent />
        <FinanceCard testId="finance-volume" label="Total Volume" value={formatEUR(volume)} sub="Since signup" icon={CreditCard} />
      </div>

      <section className="cc-surface rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="cc-overline">Escrow</div>
            <h2 className="text-lg font-semibold text-white mt-1">Funds held securely</h2>
          </div>
          <ShieldCheck className="text-[#00ff88]" />
        </div>
        <div className="flex items-center gap-3">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.min(100, Math.round((escrowTotal / Math.max(1, volume)) * 100))}
            data-testid="escrow-progress"
            className="flex-1 h-2 bg-[#06060c] rounded-full overflow-hidden border border-[#15152a]"
          >
            <div
              className="h-full bg-gradient-to-r from-[#ffaa00] to-[#00ff88] transition-all"
              style={{ width: `${Math.min(100, (escrowTotal / Math.max(1, volume)) * 100)}%` }}
            />
          </div>
          <div className="font-mono text-xs text-[#a0a0b0]">
            {inEscrow.length}/{orders.length} orders
          </div>
        </div>
        <p className="text-xs text-[#a0a0b0] mt-4 leading-relaxed max-w-2xl">
          Funds are held securely until you approve the delivery. No transfer happens until the order reaches "Completed".
        </p>
      </section>

      {/* Payment methods */}
      <section className="cc-surface rounded-2xl p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="cc-overline">Payment methods on file</div>
          <button data-testid="add-payment-btn" className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1">
            <Plus size={12} /> Add method
          </button>
        </div>
        <div className="space-y-2">
          <div className="cc-elevated rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-10 h-7 rounded bg-[#0d0d1a] border border-[#15152a] flex items-center justify-center text-[11px] text-[#00bbff]">VISA</span>
              <div>
                <div className="text-sm text-white font-mono">•••• •••• •••• 4242</div>
                <div className="text-[10px] text-[#606070] uppercase tracking-widest">Expires 12/27 · Default</div>
              </div>
            </div>
            <span className="cc-chip text-[#00ff88] border-[#00ff88]/30">active</span>
          </div>
        </div>
      </section>

      <section className="cc-surface rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#15152a]">
          <div className="cc-overline">Transactions & Invoices</div>
          <button data-testid="invoice-download-btn" className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1">
            <Download size={12} /> Export CSV
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#606070] border-b border-[#15152a]">
              <th className="px-5 py-3">Order ID</th>
              <th className="px-5 py-3">Creator</th>
              <th className="px-5 py-3">Description</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Amount</th>
              <th className="px-5 py-3 text-right">Invoice</th>
              <th className="px-5 py-3 text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} data-testid={`tx-row-${o.id}`} className="border-b border-[#15152a] hover:bg-[#0a0a14]/50">
                <td className="px-5 py-4 font-mono">
                  <Link to={`/orders/${o.id}`} className="text-[#00ff88] hover:underline">{o.id}</Link>
                </td>
                <td className="px-5 py-4 text-white">{o.creator_name}</td>
                <td className="px-5 py-4 text-[#a0a0b0]">{o.content_type} · {o.game}</td>
                <td className="px-5 py-4"><StatusPill status={o.status} /></td>
                <td className="px-5 py-4 text-right font-mono text-white">${o.price}</td>
                <td className="px-5 py-4 text-right">
                  <button className="text-[11px] text-[#00ff88] hover:underline">INV-{o.id.replace("ORD-", "")}.pdf</button>
                </td>
                <td className="px-5 py-4 text-right text-[11px] text-[#606070]">{timeAgo(o.updated_at)}</td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-[#606070]">No transactions.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

const FinanceCard = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <div data-testid={testId} className="cc-surface cc-card-hover rounded-2xl p-6">
    <div className="flex items-start justify-between">
      <div>
        <div className="cc-overline">{label}</div>
        <div className={`font-mono text-3xl mt-3 ${accent ? "text-[#00ff88]" : "text-white"}`}>{value}</div>
        <div className="text-[11px] text-[#606070] mt-1">{sub}</div>
      </div>
      <span className="w-9 h-9 rounded-lg bg-[#06060c] border border-[#15152a] flex items-center justify-center text-[#00ff88]">
        <Icon size={16} />
      </span>
    </div>
  </div>
);
